import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Notebook } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { publicApi } from '@/lib/api';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';
import { useSeo } from '@/lib/seo';
import { RECAPS } from '@/constants/testIds';

export default function RecapsListPage() {
  useSeo({
    title: 'Monthly recaps | A Guy Who Hunts',
    description:
      'Season recap digests — every published month of Field Notes gathered into a single, share-ready page.',
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi
      .recaps()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="relative border-b border-[hsl(var(--border))] hero-radial">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="kicker">Monthly recaps</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight">
            A season, one month at a time.
          </h1>
          <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Every published Field Note gathered into a monthly digest — easy to skim, easy to
            share, honest about what happened and what didn&apos;t.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 pb-20">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-10 text-center">
            <p className="font-serif text-2xl">No recaps published yet.</p>
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">
              Recaps show up here as the season fills in month by month.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((r) => (
              <Link
                key={r.year_month}
                to={`/recap/${r.year_month}`}
                data-testid={RECAPS.listCard}
                className="group"
              >
                <Card className="h-full overflow-hidden border-[hsl(var(--border))] card-elev card-elev-hover">
                  <AspectRatio ratio={4 / 3}>
                    <img
                      src={absoluteImageUrl(r.cover_image?.url)}
                      alt={r.cover_image?.alt || r.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </AspectRatio>
                  <CardContent className="p-5">
                    <p className="kicker">Recap</p>
                    <h2 className="font-serif text-xl leading-snug mt-1 tracking-tight">
                      {r.title}
                    </h2>
                    {r.intro ? (
                      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">
                        {r.intro}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] italic">
                        Everything worth remembering from the month.
                      </p>
                    )}
                    <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                      <Notebook size={14} /> {r.note_count} {r.note_count === 1 ? 'note' : 'notes'}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
